import { useEffect, useState } from "react";
import { type SortingState, type Updater } from "@tanstack/react-table";
import { connect } from "react-redux";
import { type RouteComponentProps, withRouter } from "react-router-dom";
import { Plus, Search, Warehouse, X } from "lucide-react";
import { listAggregatorList, listAggregatorListReset } from "../../actions";
import { getCountryObjectFromCache } from "../../utils";
import List, { AGGREGATOR_SORT_OPTIONS, type AggregatorListItem } from "./components/List";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "../../components/ui/input-group";
import { WFPButton } from "../../components/ui/button";
import { useTranslation } from "react-i18next";
import { type Dispatch, type UnknownAction } from "redux";
import { SortMenu } from "@/components/SortMenu";
import TimeRangeTabs from "@/components/TimeRangeTabs";
import { OrgStatusTabs } from "@/components/OrgStatusTabs";
import { DEFAULT_TIME_RANGE_PERIOD, type TimeRangePeriod } from "@/constants/timeRange";
import { DEFAULT_ORG_STATUS, type OrgStatus } from "@/constants/orgStatus";
import { isExternalUser, type UserData } from "@/utils/roles";

type AggregatorListResponse =
  | AggregatorListItem[]
  | {
      aggregator_list?: {
        count?: number;
        results?: AggregatorListItem[];
      };
      count?: number;
      results?: AggregatorListItem[];
      [key: string]: unknown;
    };

interface ListAggregatorListState {
  isFetching: boolean;
  errorMessage: string;
  data: AggregatorListResponse;
}

interface RootState {
  listAggregatorList: ListAggregatorListState;
  userDetail: { data: UserData };
}

interface StateProps {
  isFetching: boolean;
  errorMessage: string;
  aggregators: AggregatorListItem[];
  currentUser: UserData;
}

interface DispatchProps {
  listAggregatorList: (period: TimeRangePeriod, status: OrgStatus) => void;
  listAggregatorListReset: () => void;
}

type AggregatorsProps = StateProps & DispatchProps & RouteComponentProps;

type AppDispatch = Dispatch<UnknownAction>;

const dispatchAction = (dispatch: AppDispatch, action: unknown) =>
  dispatch(action as UnknownAction);

const getAggregatorListResults = (data: AggregatorListResponse) => {
  const listData = Array.isArray(data) ? data : (data.aggregator_list ?? data);

  if (Array.isArray(listData)) {
    return listData;
  }

  return listData.results ?? [];
};

export const Aggregators = ({
  isFetching,
  errorMessage,
  aggregators,
  currentUser,
  listAggregatorList,
  listAggregatorListReset,
  history,
}: AggregatorsProps) => {
  const { t } = useTranslation();

  const countryName = getCountryObjectFromCache().name;
  const canManage = !isExternalUser(currentUser);

  const [period, setPeriod] = useState<TimeRangePeriod>(DEFAULT_TIME_RANGE_PERIOD);
  const [status, setStatus] = useState<OrgStatus>(DEFAULT_ORG_STATUS);
  const [searchTerm, setSearchTerm] = useState("");
  const [sorting, setSorting] = useState<SortingState>([]);
  const selectedSort = sorting[0];
  const selectedSortOption = AGGREGATOR_SORT_OPTIONS.find(
    sortOption => sortOption.id === selectedSort?.id
  );
  const selectedSortColumn = selectedSortOption?.id || "";
  const selectedSortDirection = selectedSort?.desc ? "desc" : "asc";

  // Fetch on mount and whenever the selected time range or status changes
  useEffect(() => {
    listAggregatorList(period, status);
    return () => {
      listAggregatorListReset();
    };
  }, [listAggregatorList, listAggregatorListReset, period, status]);

  const onClickAdd = () => {
    history.push("/wfpManager/aggregatorDetails");
  };

  const onClickView = (aggregator: AggregatorListItem) => {
    if (aggregator.id === undefined || aggregator.id === null) {
      return;
    }

    history.push(`/wfpManager/aggregators/${aggregator.id}`);
  };

  const clearFilter = () => {
    setSearchTerm("");
  };

  const onSortingChange = (updater: Updater<SortingState>) => {
    setSorting(currentSorting =>
      typeof updater === "function" ? updater(currentSorting) : updater
    );
  };

  const onSortColumnChange = (sortColumn: string) => {
    setSorting([
      {
        id: sortColumn,
        desc: selectedSortDirection === "desc",
      },
    ]);
  };

  const onSortDirectionChange = (sortDirection: string) => {
    setSorting([
      {
        id: selectedSortColumn || AGGREGATOR_SORT_OPTIONS[0].id,
        desc: sortDirection === "desc",
      },
    ]);
  };

  return (
    <main className="flex flex-col gap-6 py-4 px-6 sm:py-6 sm:px-8 md:py-8 md:px-10">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-x-2">
          <span className="inline-flex shrink-0 items-center justify-center size-10 rounded-lg p-2 text-ivory-600 bg-ivory-100">
            <Warehouse />
          </span>
          <h1 className="font-semibold text-2xl">
            {t("aggregators.pageTitle", { country: countryName })}
          </h1>
        </div>
        {canManage && (
          <WFPButton onClick={onClickAdd}>
            <Plus className="size-4" aria-hidden="true" />
            {t("aggregators.newAggregator")}
          </WFPButton>
        )}
      </div>
      <OrgStatusTabs
        value={status}
        onValueChange={setStatus}
        ariaLabel={t("aggregators.status.ariaLabel")}
        activeLabel={t("aggregators.status.active")}
        archivedLabel={t("aggregators.status.archived")}
      />
      <div className="flex flex-wrap items-center gap-2">
        <InputGroup className="w-full max-w-xs sm:w-80">
          <InputGroupAddon>
            <Search className="size-4" aria-hidden="true" />
          </InputGroupAddon>
          <InputGroupInput
            id="aggregator-name-input"
            type="search"
            value={searchTerm}
            className="[&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden"
            placeholder={t("aggregators.list.searchPlaceholder", "Search by name or area")}
            aria-label={t("aggregators.list.searchPlaceholder", "Search by name or area")}
            onChange={event => setSearchTerm(event.target.value)}
          />
          {searchTerm && (
            <InputGroupAddon align="inline-end">
              <InputGroupButton
                type="button"
                variant="ghost"
                size="icon-xs"
                aria-label={t("commodities.clearFilter")}
                onClick={clearFilter}
              >
                <X className="size-3.5" aria-hidden="true" />
              </InputGroupButton>
            </InputGroupAddon>
          )}
        </InputGroup>
        <SortMenu
          options={AGGREGATOR_SORT_OPTIONS}
          sortColumn={selectedSortColumn}
          sortDirection={selectedSortDirection}
          onSortColumnChange={onSortColumnChange}
          onSortDirectionChange={onSortDirectionChange}
        />
        <TimeRangeTabs
          className="ml-auto shrink-0"
          value={period}
          onValueChange={setPeriod}
          ariaLabel={t("aggregators.timeRange.ariaLabel")}
        />
      </div>
      <List
        aggregators={aggregators}
        errorMessage={errorMessage}
        isFetching={isFetching}
        onClickView={onClickView}
        onSortingChange={onSortingChange}
        searchTerm={searchTerm}
        sorting={sorting}
      />
    </main>
  );
};

export const mapStateToProps = (state: RootState): StateProps => {
  return {
    isFetching: state.listAggregatorList.isFetching,
    errorMessage: state.listAggregatorList.errorMessage,
    aggregators: getAggregatorListResults(state.listAggregatorList.data),
    currentUser: state.userDetail?.data ?? { user_groups: [] },
  };
};

export const mapDispatchToProps = (dispatch: AppDispatch): DispatchProps => {
  return {
    listAggregatorList: (period: TimeRangePeriod, status: OrgStatus) =>
      dispatchAction(dispatch, listAggregatorList({ period, status })),
    listAggregatorListReset: () => dispatchAction(dispatch, listAggregatorListReset()),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(Aggregators));
